import { toolkitCx } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, HTMLAttributes, Suspense, lazy, useMemo } from "react";

import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { EOrderFlowState, useOrderContext } from "../../../stores/OrderContext";
import LoadingComponent from "../../LoadingComponent";
import MaybeTasteTestContributeWidgetOverlay from "../MaybeTasteTestContributeWidgetOverlay";
import TasteTestDepositSetup from "../TasteTestDepositSetup";
import TasteTestOrderSuccessView from "../TasteTestOrderSuccessView";
import FadeInBasic from "../../FadeInView/FadeInBasic";

const LazyTasteTestOrderSubmitting = lazy(
  () => import(/* webpackChunkName: "TransactionSubmitting" */ "../TasteTestOrderSubmitting")
);
const LazyOrderError = lazy(
  () => import(/* webpackChunkName: "OrderError" */ "../../OrderComponents/OrderError")
);

interface ITasteTestContributeWidgetProps extends HTMLAttributes<HTMLDivElement> {
  isSticky?: boolean;
}

const TasteTestDepositWidget: FC<ITasteTestContributeWidgetProps> = ({ isSticky }) => {
  const {
    state: {
      flowData: { flowState },
    },
  } = useOrderContext();
  const {
    state: {
      tasteTestStatus: { isTasteTestInactive, isTasteTestReadOnly },
    },
  } = useTasteTestContext();
  /*const isButtonDisabled =
    givenExceedsBalance || isTasteTestInactive || isTasteTestPending || !given?.amount;*/

  const memoizedDepositFlowNode = useMemo(() => {
    /* -------------------------------------------------------------------------------------------------
     * Submitting UI
     * -----------------------------------------------------------------------------------------------*/
    if (flowState === EOrderFlowState.submitting) {
      return (
        <Suspense fallback={<LoadingComponent />}>
          <LazyTasteTestOrderSubmitting />
        </Suspense>
      );
    }

    /* -------------------------------------------------------------------------------------------------
     * Order Status (Error) UI
     * -----------------------------------------------------------------------------------------------*/
    if (EOrderFlowState.error === flowState) {
      return (
        <Suspense fallback={<LoadingComponent />}>
          <LazyOrderError />
        </Suspense>
      );
    }

    /* -------------------------------------------------------------------------------------------------
     * Order Status (Success) UI
     * -----------------------------------------------------------------------------------------------*/
    if (EOrderFlowState.success === flowState) {
      return <TasteTestOrderSuccessView />;
    }

    return <TasteTestDepositSetup />;
  }, [flowState]);

  return (
    <>
      {/* -------------------------------------------------------------------------------------------------
       * Widget Wrapper
       * -----------------------------------------------------------------------------------------------*/}
      <FadeInBasic
        className={cx(
          "relative rounded-lg",
          {
            "animation-delay-100 motion-safe:animate-shadow-pulse": isSticky,
          },
          toolkitCx.layout.background.default
        )}
      >
        {/* -------------------------------------------------------------------------------------------------
         * Renders the overlay with the warning/info message on why the widget is disabled.
         * -----------------------------------------------------------------------------------------------*/}
        <MaybeTasteTestContributeWidgetOverlay />

        <div
          className={cx("relative z-10", {
            "pointer-events-none blur-sm": isTasteTestInactive || isTasteTestReadOnly,
          })}
        >
          {/* -------------------------------------------------------------------------------------------------
           * Widget Content
           * -----------------------------------------------------------------------------------------------*/}
          <div
            className={cx("flex min-h-[18rem] w-full justify-center", {
              "items-start": [
                EOrderFlowState.initial,
                EOrderFlowState.previewCancelled,
                EOrderFlowState.reset,
              ].includes(flowState),
              "items-center": ![
                EOrderFlowState.initial,
                EOrderFlowState.previewCancelled,
                EOrderFlowState.reset,
              ].includes(flowState),
            })}
          >
            {memoizedDepositFlowNode}
          </div>
        </div>
      </FadeInBasic>
    </>
  );
};

export default TasteTestDepositWidget;
